import {
  WS_METHODS,
  type PreviewControlAction,
  type PreviewControlLeaseId,
  type PreviewControlStatus,
} from "@t3tools/contracts";
import { Atom } from "effect/unstable/reactivity";

import type { EnvironmentRegistry } from "../connection/registry.ts";
import {
  createAtomCommandScheduler,
  createEnvironmentRpcCommand,
  createEnvironmentRpcQueryAtomFamily,
  createEnvironmentRpcSubscriptionAtomFamily,
  type AtomCommandResult,
} from "./runtime.ts";

export const previewAutomationHostFocusConcurrencyKey = (value: {
  readonly environmentId: string;
  readonly input: {
    readonly clientId: string;
    readonly connectionId: string;
  };
}): string => JSON.stringify([value.environmentId, value.input.clientId, value.input.connectionId]);

export const previewControlConcurrencyKey = (value: {
  readonly environmentId: string;
  readonly input: { readonly threadId: string; readonly tabId: string };
}): string => JSON.stringify([value.environmentId, value.input.threadId, value.input.tabId]);

export const previewControlInputConcurrencyKey = (value: {
  readonly environmentId: string;
  readonly input: { readonly threadId: string; readonly tabId: string; readonly leaseId: string };
}): string =>
  JSON.stringify([
    value.environmentId,
    value.input.threadId,
    value.input.tabId,
    value.input.leaseId,
  ]);

export const MAX_PENDING_PREVIEW_CONTROL_INPUTS = 64;

type PreviewControlInputResult = AtomCommandResult<unknown, unknown>;

interface PreviewControlInputQueueOptions {
  readonly send: (
    leaseId: PreviewControlLeaseId,
    action: PreviewControlAction,
  ) => Promise<PreviewControlInputResult>;
  readonly maxPending?: number;
}

interface PreviewControlInputQueueItem {
  readonly leaseId: PreviewControlLeaseId;
  readonly action: PreviewControlAction;
  readonly resolve: (result: PreviewControlInputResult | null) => void;
}

interface PreviewControlInputQueueLane {
  readonly generation: number;
  readonly leaseId: PreviewControlLeaseId;
  readonly pending: Array<PreviewControlInputQueueItem>;
  inFlight: boolean;
}

export interface PreviewControlInputQueue {
  /** Switches the queue to a lease and drops all pending input from older leases. */
  readonly setLease: (leaseId: PreviewControlLeaseId | null) => void;
  /** Enqueues one action, or resolves to null when the lease is no longer current/full. */
  readonly enqueue: (
    leaseId: PreviewControlLeaseId,
    action: PreviewControlAction,
  ) => Promise<PreviewControlInputResult | null>;
  /** Invalidates the current generation and drops all pending input. */
  readonly cancel: () => void;
}

/**
 * Serializes valid browser input without allowing stale lease work to hold up
 * a newly acquired lease. An already-running RPC cannot be withdrawn from the
 * transport, so its result is suppressed after invalidation and a new
 * generation gets its own lane immediately.
 */
export function createPreviewControlInputQueue(
  options: PreviewControlInputQueueOptions,
): PreviewControlInputQueue {
  const maxPending = Math.max(1, options.maxPending ?? MAX_PENDING_PREVIEW_CONTROL_INPUTS);
  const lanes = new Map<number, PreviewControlInputQueueLane>();
  let generation = 0;
  let activeLeaseId: PreviewControlLeaseId | null = null;

  const resolvePending = (lane: PreviewControlInputQueueLane): void => {
    for (const item of lane.pending.splice(0)) {
      item.resolve(null);
    }
  };

  const pump = (lane: PreviewControlInputQueueLane): void => {
    if (lane.inFlight) return;
    const item = lane.pending.shift();
    if (item === undefined) {
      if (lanes.get(lane.generation) === lane) lanes.delete(lane.generation);
      return;
    }

    lane.inFlight = true;
    void Promise.resolve()
      .then(() => options.send(item.leaseId, item.action))
      .then(
        (result) => {
          const isCurrent = lane.generation === generation && activeLeaseId === lane.leaseId;
          item.resolve(isCurrent ? result : null);
        },
        () => item.resolve(null),
      )
      .finally(() => {
        lane.inFlight = false;
        pump(lane);
      });
  };

  const setLease = (leaseId: PreviewControlLeaseId | null): void => {
    if (activeLeaseId === leaseId) return;
    const previousLane = lanes.get(generation);
    if (previousLane !== undefined) resolvePending(previousLane);
    generation += 1;
    activeLeaseId = leaseId;
    if (leaseId !== null) {
      lanes.set(generation, {
        generation,
        leaseId,
        pending: [],
        inFlight: false,
      });
    }
  };

  return {
    setLease,
    enqueue: (leaseId, action) => {
      if (activeLeaseId !== leaseId) return Promise.resolve(null);
      const lane = lanes.get(generation);
      if (lane === undefined || lane.pending.length >= maxPending) return Promise.resolve(null);
      return new Promise<PreviewControlInputResult | null>((resolve) => {
        lane.pending.push({ leaseId, action, resolve });
        pump(lane);
      });
    },
    cancel: () => setLease(null),
  };
}

export type PreviewControlLeaseReconciliation = "wait" | "drop" | "resume";

/** Keeps reconnect behavior identical across web and mobile control surfaces. */
export function reconcilePreviewControlLease(input: {
  readonly hasLease: boolean;
  readonly pending: boolean;
  readonly resumeInFlight: boolean;
  readonly status: Pick<
    PreviewControlStatus,
    "available" | "mode" | "heldByCurrentSession" | "canTakeControl"
  > | null;
}): PreviewControlLeaseReconciliation {
  const { status } = input;
  if (!input.hasLease || !status?.available || input.pending || input.resumeInFlight) {
    return "wait";
  }
  // A Return that loses its transport must never turn into an automatic Take.
  if (status.mode === "returning") return "drop";
  // The watch stream can still contain the frame captured immediately before
  // a successful Take response. Retain the opaque id, but do not issue a
  // second Take from that stale frame. If the lease really expired, the user
  // can explicitly Take again and the server will mint a fresh lease.
  if (status.mode === "watch") return "wait";
  if (!status.heldByCurrentSession) return "drop";
  if (status.canTakeControl) return "wait";
  return "resume";
}

export function createPreviewEnvironmentAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | R, E>,
) {
  const lifecycleScheduler = createAtomCommandScheduler();
  const statusScheduler = createAtomCommandScheduler();
  const automationScheduler = createAtomCommandScheduler();
  const controlLifecycleScheduler = createAtomCommandScheduler();
  const controlHeartbeatScheduler = createAtomCommandScheduler();
  const controlInputScheduler = createAtomCommandScheduler();
  const lifecycleConcurrency = {
    mode: "serial" as const,
    key: ({ environmentId, input }: { environmentId: string; input: { threadId: string } }) =>
      JSON.stringify([environmentId, input.threadId]),
  };
  return {
    list: createEnvironmentRpcQueryAtomFamily(runtime, {
      label: "environment-data:preview:list",
      tag: WS_METHODS.previewList,
      staleTimeMs: 5_000,
    }),
    events: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:preview:events",
      tag: WS_METHODS.subscribePreviewEvents,
    }),
    discoveredServers: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:preview:discovered-servers",
      tag: WS_METHODS.subscribeDiscoveredLocalServers,
      // Configured URLs are part of this atom's key. Dispose immediately so
      // unmounted projects stop contributing probe candidates on the server.
      idleTtlMs: 0,
    }),
    automationRequests: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:preview:automation-requests",
      tag: WS_METHODS.previewAutomationConnect,
      // Automation requests are commands, not cached query data. Dispose the
      // stream immediately with its owner so stale requests cannot replay when
      // a thread remounts and the server can clear disconnected hosts promptly.
      idleTtlMs: 0,
    }),
    controlWatch: createEnvironmentRpcSubscriptionAtomFamily(runtime, {
      label: "environment-data:preview:control-watch",
      tag: WS_METHODS.previewControlWatch,
      // Frames are live view state. Never replay a stale browser image after
      // the surface unmounts or its environment reconnects.
      idleTtlMs: 0,
    }),
    open: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:open",
      tag: WS_METHODS.previewOpen,
      scheduler: lifecycleScheduler,
      concurrency: lifecycleConcurrency,
    }),
    navigate: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:navigate",
      tag: WS_METHODS.previewNavigate,
      scheduler: lifecycleScheduler,
      concurrency: lifecycleConcurrency,
    }),
    resize: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:resize",
      tag: WS_METHODS.previewResize,
      scheduler: lifecycleScheduler,
      concurrency: lifecycleConcurrency,
    }),
    refresh: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:refresh",
      tag: WS_METHODS.previewRefresh,
      scheduler: lifecycleScheduler,
      concurrency: lifecycleConcurrency,
    }),
    close: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:close",
      tag: WS_METHODS.previewClose,
      scheduler: lifecycleScheduler,
      concurrency: lifecycleConcurrency,
    }),
    reportStatus: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:report-status",
      tag: WS_METHODS.previewReportStatus,
      scheduler: statusScheduler,
      concurrency: {
        mode: "latest",
        key: ({ environmentId, input }) =>
          JSON.stringify([environmentId, input.threadId, input.tabId]),
      },
    }),
    respondToAutomation: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:automation-respond",
      tag: WS_METHODS.previewAutomationRespond,
      scheduler: automationScheduler,
      concurrency: {
        mode: "singleFlight",
        key: ({ environmentId, input }) =>
          JSON.stringify([environmentId, input.connectionId, input.requestId]),
      },
    }),
    focusAutomationHost: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:automation-focus-host",
      tag: WS_METHODS.previewAutomationFocusHost,
      scheduler: automationScheduler,
      concurrency: {
        mode: "latest",
        key: previewAutomationHostFocusConcurrencyKey,
      },
    }),
    takeControl: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:control-take",
      tag: WS_METHODS.previewControlTake,
      scheduler: controlLifecycleScheduler,
      concurrency: { mode: "serial", key: previewControlConcurrencyKey },
    }),
    heartbeatControl: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:control-heartbeat",
      tag: WS_METHODS.previewControlHeartbeat,
      scheduler: controlHeartbeatScheduler,
      concurrency: { mode: "latest", key: previewControlConcurrencyKey },
    }),
    returnControl: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:control-return",
      tag: WS_METHODS.previewControlReturn,
      scheduler: controlLifecycleScheduler,
      concurrency: { mode: "serial", key: previewControlConcurrencyKey },
    }),
    dispatchControl: createEnvironmentRpcCommand(runtime, {
      label: "environment-data:preview:control-dispatch",
      tag: WS_METHODS.previewControlDispatch,
      scheduler: controlInputScheduler,
      concurrency: { mode: "serial", key: previewControlInputConcurrencyKey },
    }),
  };
}
